import React, { useEffect, useState } from 'react';
import styles from '../../../src/assets/scss/CreateOrders.module.scss';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { FaEdit, FaTrash } from 'react-icons/fa';
import Pagination from '../../Components/Common/Pagination';
import DeleteConfirmModal from '../../Components/Common/DeleteConfirmModal';
import PopupWrapper from '../../Components/Common/PopupWrapper';
import AddItem from './AddItem';
import {
    useGetMyVendorsQuery,
    useGetMyOrderByIdQuery,
    useUpdateOrderAdminMutation
} from '../../slices/admin/admin';
import Select from 'react-select';

const UpdateMyOrder = () => {
    const [effectiveDate, setEffectiveDate] = useState('');
    const [selectedStore, setSelectedStore] = useState('');
    const [selectedVendorId, setSelectedVendorId] = useState('');
    const [orderItems, setOrderItems] = useState([]);
    const [deliveryOption, setDeliveryOption] = useState('');
    const [showAddItemPopup, setShowAddItemPopup] = useState(false);
    const [popupProps, setPopupProps] = useState(null);
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedDeleteIndex, setSelectedDeleteIndex] = useState(null);
    const [storeUser, setStoreUser] = useState(null);

    const [searchParams] = useSearchParams();

    const goBackWithTab = () => {
        const activeTab = searchParams.get("tab") || "myInvoice";
        navigate(`/dashboard/Ordertable?tab=${activeTab}`);
    };

    const navigate = useNavigate();
    const location = useLocation();
    const orderId = location.state?.orderId || null;
    const isUpdateMode = location.state?.updateMode || false;

    const { data: orderData, isLoading, refetch } = useGetMyOrderByIdQuery(orderId, {
        skip: !orderId,
    });
    const { data: vendorsData } = useGetMyVendorsQuery();
    const [updateOrder, { isLoading: isUpdating }] = useUpdateOrderAdminMutation();
    const stores = vendorsData?.stores || [];

    useEffect(() => {
        if (!isUpdateMode || !orderId || !orderData?.data?.[0]) return;

        const order = orderData.data[0];
        setOrderItems(
            order.items?.map(item => ({
                itemId: item?._id,
                itemName: item?.productName ?? 'Unnamed Item',
                bag: item?.productBag ?? '',
                productType: item?.productType ?? 'raw',
                productQuantity: item?.productBag === 'kilogram' ? item?.productQuantity ?? 0 : 0,
                productCount: item?.productBag !== 'kilogram' ? item?.productCount ?? 0 : 0,
            })) || []
        );

        setEffectiveDate(dayjs(order.date || order.preOrderDate).format('YYYY-MM-DD'));
        const matchedStoreId = order.orderStore?._id || order.orderStore;
        if (matchedStoreId) {
            setSelectedStore(matchedStoreId);
            setSelectedVendorId(matchedStoreId);
        } else {
            toast.warn('Order store not found');
        }
        setDeliveryOption(order.deliveryOption || '');
        setStoreUser(order.orderStore?.user?.[0] || null);
    }, [isUpdateMode, orderId, orderData]);

    const handleAddItem = () => {
        if (!selectedVendorId) {
            toast.error('Please select a store first');
            return;
        }
        setPopupProps({
            storeId: selectedVendorId,
            updateMode: isUpdateMode,
            orderId: orderId
        });
        setShowAddItemPopup(true);
    };

    const handleQuantityChange = (index, value) => {
        const updatedItems = [...orderItems];
        updatedItems[index].productQuantity = Number(value);
        setOrderItems(updatedItems);
    };

    const handleCountChange = (index, value) => {
        const updated = [...orderItems];
        updated[index].productCount = Number(value);
        setOrderItems(updated);
    };

    const handleEditItem = (index) => {
        const item = orderItems[index];
        if (!item?.itemId && isUpdateMode) {
            toast.error('Item ID missing');
            return;
        }
        setPopupProps({
            storeId: selectedVendorId,
            editIndex: index,
            itemData: item,
            updateMode: isUpdateMode,
            orderId: orderId,
            itemId: item.itemId
        });
        setShowAddItemPopup(true);
    };

    const handleDelete = (index) => {
        setSelectedDeleteIndex(index);
        setModalOpen(true);
    };

    const confirmDelete = () => {
        if (selectedDeleteIndex !== null) {
            const updatedItems = [...orderItems];
            updatedItems.splice(selectedDeleteIndex, 1);
            setOrderItems(updatedItems);
            setSelectedDeleteIndex(null);
            setModalOpen(false);
        }
    };

    const handleSave = async () => {
        const products = orderItems.map((item, index) => {
            const base = {
                productName: item.itemName,
                productBag: item.bag,
                productType: item.productType || 'raw',
            };

            if (item.bag === 'kilogram') {
                if (!item.productQuantity || item.productQuantity < 1) {
                    throw new Error(`Item ${index + 1}: Quantity must be ≥ 1`);
                }
                return {
                    ...base,
                    productQuantity: Number(item.productQuantity),
                    productCount: 0,
                };
            } else {
                if (!item.productCount || item.productCount < 1) {
                    throw new Error(`Item ${index + 1}: Count must be ≥ 1`);
                }
                return {
                    ...base,
                    productQuantity: 0,
                    productCount: Number(item.productCount),
                };
            }
        });

        try {
            if (isUpdateMode && orderId) {
                const updatePayload = {
                    preOrderDate: effectiveDate,
                    orderStore: selectedVendorId,
                    deliveryOption,
                    notes: 'extra delivery required',
                    products
                };
                await updateOrder({ id: orderId, body: updatePayload }).unwrap();
                toast.success('Order updated successfully');
                goBackWithTab();
                //navigate('/dashboard/Ordertable');
            }
        } catch (err) {
            toast.error(err?.data?.message || 'Update failed');
        }
    };

    const storeOptions = stores.map(store => ({
        value: store._id,
        label: `${store.storeName}`
    }));





    const itemsPerPage = 10;
    const [currentPage, setCurrentPage] = useState(1);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedItems = orderItems.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(orderItems.length / itemsPerPage);

    if (isLoading) return <div className={styles.loading}>Loading order...</div>;

    return (
        <div className={styles.createOrdersContainer}>
            <div className={styles.header}>
                <h2 className={styles.tittle}>Update Order</h2>
                <span className={styles.closeIcon}
                    //onClick={() => navigate('/dashboard/Ordertable')}
                    onClick={goBackWithTab}
                >✕</span>
            </div>

            <div className={styles.containers}>
                <div className={styles.formSection}>
                    <div className={styles.formLeft}>
                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>Order Placed To</label>
                            <div className={styles.selectWrapper}>
                                <Select
                                    className={styles.customSelect}
                                    styles={{
                                        control: (base) => ({
                                            ...base,
                                            border: '1px solid #6D6D6D',
                                            borderRadius: '6px',
                                            fontSize: '16px',
                                            minHeight: '50px',
                                            boxShadow: 'none',
                                            color: 'black',
                                            fontWeight: '600'
                                        }),
                                        placeholder: (base) => ({
                                            ...base,
                                            color: '#6D6D6D',
                                            fontSize: '16px',
                                            fontWeight: '600'
                                        })
                                    }}
                                    value={storeOptions.find(option => option.value === selectedStore) || null}
                                    onChange={(selectedOption) => {
                                        setSelectedStore(selectedOption?.value);
                                        setSelectedVendorId(selectedOption?.value);
                                    }}
                                    options={storeOptions}
                                    placeholder="Select Store"
                                    isSearchable
                                />
                            </div>
                        </div>

                        {/* <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>Order Status</label>
                            <select className={styles.inputs} disabled>
                                <option>Pending</option>
                            </select>
                        </div> */}

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>Pre Order Date</label>
                            <DatePicker
                                selected={effectiveDate ? dayjs(effectiveDate, 'YYYY-MM-DD').toDate() : null}
                                onChange={(date) => setEffectiveDate(dayjs(date).format('YYYY-MM-DD'))}
                                dateFormat="MM.dd.yyyy"
                                minDate={new Date()}
                                placeholderText="Select date"
                                className={styles.input}
                                wrapperClassName={styles.fullWidthDatepicker}
                            />
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>Delivery Option</label>
                            <select
                                className={styles.inputs}
                                value={deliveryOption}
                                onChange={(e) => setDeliveryOption(e.target.value)}
                                required
                            >
                                <option value="">Select Delivery Option</option>
                                <option value="pickup">Own delivery pickup</option>
                                <option value="shipping">Send by store</option>
                                <option value="third-party logistics">Third-Party Logistics</option>
                            </select>
                        </div>
                    </div>

                    <div className={styles.formRight}>
                        <p><strong>Order Created By</strong></p>
                        <p><strong>Name:</strong> {`${orderData?.data?.[0]?.createdBy?.firstName || ''} ${orderData?.data?.[0]?.createdBy?.lastName || ''}`.trim() || 'N/A'}</p>
                        <p><strong>Email:</strong> {orderData?.data?.[0]?.createdBy?.email || 'N/A'}</p>
                        <p><strong>Phone No:</strong> {orderData?.data?.[0]?.createdBy?.phone || 'N/A'}</p>
                    </div>
                </div>

                <div className={styles.tableHeader}>
                    <button className={styles.addBtn} onClick={handleAddItem}>+ Add Item</button>
                </div>

                <div className={styles.tableSection}>
                    <table>
                        <thead>
                            <tr>
                                <th><input type="checkbox" /></th>
                                <th>Order Seq No</th>
                                <th>Item Category</th>
                                <th>Item Scales</th>
                                <th>Product Type</th>
                                <th>Item Quantity (kg)</th>
                                <th>Item Count</th>
                                <th>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {paginatedItems.map((item, index) => {
                                if (!item) return null;
                                return (
                                    <tr key={index}>
                                        <td><input type="checkbox" /></td>
                                        <td>O{startIndex + index + 1}</td>
                                        <td>{item.itemName}</td>
                                        <td>{item.bag}</td>
                                        <td>{item.productType}</td>
                                        <td>
                                            {item.bag === 'kilogram' ? (
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className={styles.tableInput}
                                                    value={item.productQuantity || ''}
                                                    onChange={(e) => handleQuantityChange(startIndex + index, e.target.value)}
                                                />
                                            ) : '-'}
                                        </td>
                                        <td>
                                            {item.bag !== 'kilogram' ? (
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className={styles.tableInput}
                                                    value={item.productCount || ''}
                                                    onChange={(e) => handleCountChange(startIndex + index, e.target.value)}
                                                />
                                            ) : '-'}
                                        </td>
                                        <td className={styles.actions}>
                                            <FaEdit className={styles.icon} title="Edit" onClick={() => handleEditItem(startIndex + index)} />
                                            <FaTrash className={styles.icon} title="Delete" onClick={() => handleDelete(startIndex + index)} />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <div className={styles.paginationWrapper}>
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                    </div>
                </div>

                <DeleteConfirmModal
                    isOpen={isModalOpen}
                    onClose={() => setModalOpen(false)}
                    onConfirm={confirmDelete}
                />

                <div className={styles.footerButtons}>
                    <button className={styles.cancel}
                        // onClick={() => navigate('/dashboard/Ordertable')}
                        onClick={goBackWithTab}
                    >Cancel</button>
                    <button className={styles.saves} onClick={handleSave} disabled={isUpdating}>
                        {isUpdating ? 'Saving...' : 'Update the order'}
                    </button>
                </div>
            </div>

            {showAddItemPopup && (
                <PopupWrapper onClose={() => setShowAddItemPopup(false)}>
                    <AddItem
                        popupMode={true}
                        closePopup={() => setShowAddItemPopup(false)}
                        passedState={popupProps}
                        onItemUpdate={(newItem, index) => {
                            if (typeof index === 'number') {
                                const updated = [...orderItems];
                                updated[index] = newItem;
                                setOrderItems(updated);
                            } else {
                                setOrderItems([...orderItems, newItem]);
                            }
                        }}
                    />
                </PopupWrapper>
            )}
        </div>
    );
};

export default UpdateMyOrder;
